import { forwardRef, useImperativeHandle, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import { type HistoryItem, HistoryType } from "../types";

export type HistoryDialogProps = {
    history: HistoryItem[];
};

function formatFieldName(fname: string): string {
    return fname.split('$')[0];
}

function formatTimestamp(date?: Date): string {
    if (!date) return "";
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const min = date.getMinutes();
    const sec = date.getSeconds();
    return `${month}月${day}日${hour}時${min}分${sec}秒`;
}

function formatValue(value?: string): string {
    return value && value.length > 15
        ? value.replace(/(.{15})/g, '$1\n')
        : value ?? '';
}
function filter(histories: HistoryItem[]): HistoryItem[] {
    return histories.filter(
        h =>
            h.type === HistoryType.Send ||
            h.type === HistoryType.Receive ||
            h.type === HistoryType.Conflicted ||
            h.type === HistoryType.Resolved
    );
}

function getStatusText(history: HistoryItem): string {
    switch (history.type) {
        case HistoryType.Conflicted:
            return "送信";
        case HistoryType.Resolved:
            if (history.prev === history.next) return "受信";
            else return "送信";
        default:
            return history.type;
    }
}

function getCondition(history: HistoryItem): string {
    switch (history.type) {
        case HistoryType.Send:
        case HistoryType.Receive:
            return "完了";
        case HistoryType.Conflicted:
            return "競合";
        case HistoryType.Resolved:
            if (history.prev === history.next) return "競合解決";
            else return "再送信";
        default:
            return "";
    }
}

export type HistoryDialogHandle = {
    open: () => void;
    close: () => void;
};

/** 編集履歴ダイアログ */
export const HistoryDialog = forwardRef<HistoryDialogHandle, HistoryDialogProps>((props, ref) => {
    const { history } = props;
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    useImperativeHandle(ref, () => ({
        open: () => setIsHistoryOpen(true),
        close: () => setIsHistoryOpen(false),
    }), []);

    return (
        <Dialog open={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>編集履歴</DialogTitle>
            <DialogContent dividers>
                {filter(history).length === 0 ? (
                    <Typography color="text.secondary">履歴はありません。</Typography>
                ) : (
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" sx={{ borderBottom: 1, px: 1, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: 'center' }}>
                                    日時
                                </Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: 'center' }}>
                                    項目
                                </Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, textAlign: 'center' }}>古い値</Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, width: 32, textAlign: 'center' }}></Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, textAlign: 'center' }}>新しい値</Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, textAlign: 'center' }}>送信/受信</Box>
                                <Box component="th" sx={{ borderBottom: 1, px: 1, textAlign: 'center' }}>状況</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {filter(history)
                                .map((history, idx) => (
                                    <Box component="tr" key={history.fieldName + idx}>
                                        <Box component="td" sx={{ px: 1, py: 0.5, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: 'center' }}>
                                            <Typography noWrap>
                                                {formatTimestamp(history.timestamp)}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, maxWidth: 160, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: 'center' }}>
                                            <Typography noWrap title={history.fieldName}>
                                                {formatFieldName(history.fieldName)}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, textAlign: 'center' }}>
                                            <Typography // 古い値
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                    wordBreak: 'break-all',
                                                    maxWidth: 160,
                                                    overflowWrap: 'break-word',
                                                }}
                                            >
                                                {formatValue(history.prev)}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, textAlign: "center", color: "text.secondary" }}>→</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, textAlign: 'center' }}>
                                            <Typography // 新しい値
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                    wordBreak: 'break-all',
                                                    maxWidth: 160,
                                                    overflowWrap: 'break-word',
                                                }}
                                            >
                                                {formatValue(history.next)}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, textAlign: 'center' }}>{getStatusText(history)}</Box>
                                        <Box component="td" sx={{ px: 1, py: 0.5, textAlign: 'center' }}>{getCondition(history)}</Box>
                                    </Box>
                                ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setIsHistoryOpen(false)}>閉じる</Button>
            </DialogActions>
        </Dialog>
    );
});
